import { updateStats } from '@stores';
import { create } from 'zustand';
import api from '@api/http/api';

export interface UserData {
  id: string;
  zenith: number;
  avatar: string;
  credits: number;
  username: string;
  blocked: string[];
  guideStep: number;
  appleSub?: string;
  googleSub?: string;
  guideReady: boolean;
  tutorials: string[];
  missionLevel: number;
  premiumUntil?: string;
  role: 'MODERATOR' | 'ADMIN' | 'USER';
  chassis: {
    mods: { mod: { key: string }; modId: string; slot: number; }[];
    prestige: number;
    active: boolean;
    key: string;
    xp: number;
    id: string;
  }[];
  weapons: {
    mods: { mod: { key: string }; modId: string; slot: number; }[];
    prestige: number;
    active: boolean;
    key: string;
    xp: number;
    id: string;
  }[];
  items: {
    amount: number;
    key: string;
  }[];
  blueprints: {
    finishesAt?: string;
    amount: number;
    type: string;
    key: string;
    id: string;
  }[];
  mods: {
    level: number;
    rank: number;
    key: string;
    id: string;
  }[];
  quests: {
    type: 'DAILY_LOGIN' | 'COLLECT' | 'KILL';
    rewardAmount: string;
    collected: boolean;
    rewardType: string;
    createdAt: string;
    progress: number;
    amount: number;
    key: string;
    id: string;
  }[];
  allies: {
    type: 'CHRONOHUNTERS' | 'VOIDMERCHANTS' | 'BIOMECH';
    active: boolean;
    rank: number;
    xp: number;
    id: string;
  }[];
}

interface AppState {
  setIsLoadingImages: (isLoadingImages: boolean) => void,
  completeTutorial: (tutorial: string) => Promise<void>,
  setIsConnecting: (isConnecting: boolean) => void,
  setArsenalTab: (arsenalTab: ArsenalTab) => void,
  setIsFetching: (isFetching: boolean) => void,
  setDevice: (device: string) => void,
  refetch: () => Promise<void>,
  setId: (id: string) => void,
  setTab: (tab: Tab) => void,
  isLoadingImages: boolean,
  arsenalTab: ArsenalTab,
  isConnecting: boolean,
  isFetching: boolean,
  userData: UserData,
  device: string,
  id: string,
  tab: Tab,
}

type ArsenalTab = 'playerMarket' | 'weapons' | 'chassis' | 'market' | 'items' | 'forge' | 'mods';
type Tab = 'navigation' | 'arsenal' | 'market' | 'combat' | 'forge' | 'lobby' | 'chat' | 'hub';

export const useAppStore = create<AppState>()(
  (set) => ({
    setIsLoadingImages: (isLoadingImages) => set({ isLoadingImages }),
    setIsConnecting: (isConnecting) => set({ isConnecting }),
    setArsenalTab: (arsenalTab) => set({ arsenalTab }),
    setIsFetching: (isFetching) => set({ isFetching }),
    setDevice: (device) => set({ device }),
    setTab: (tab) => set({ tab }),
    setId: (id) => set({ id }),
    refetch: async () => {
      set({ isFetching: true });

      const data = await api.get('/users/me');
      updateStats(data);

      set({ userData: data, isFetching: false });
    },
    completeTutorial: async (tutorial: string) => {
      await api.post('/users/complete-tutorial', { tutorial });
      set((state) => ({
        userData: {
          ...state.userData,
          tutorials: state.userData.tutorials.concat(tutorial),
        },
      }));
    },
    isLoadingImages: true,
    arsenalTab: 'weapons',
    isConnecting: true,
    isFetching: false,
    userData: {
      id: '',
      mods: [],
      zenith: 0,
      items: [],
      allies: [],
      quests: [],
      credits: 0,
      blocked: [],
      chassis: [],
      weapons: [],
      guideStep: 0,
      username: '',
      role: 'USER',
      tutorials: [],
      blueprints: [],
      missionLevel: 1,
      guideReady: false,
      avatar: 'avatar1',
    },
    device: 'web',
    tab: 'hub',
    id: '',
  }),
);